/**
 * Title and body strings the API sends as Web Push and in-app rows.
 * Callers pass live names and dates. Do not paraphrase these templates.
 */

import { formatClock12h } from './time';

export function bookedInPushCopy(input: {
  swimmerName: string;
  squadName?: string | null;
  sessionDate?: string | null;
  startTime?: string | null;
  placementStatus: string;
}): { title: string; body: string } {
  const waitlistNote = input.placementStatus === 'waitlist' ? ' (waitlist)' : '';
  const whenBits = [
    input.squadName?.trim() || null,
    input.sessionDate || null,
    input.startTime ? formatClock12h(input.startTime) : null,
  ].filter((bit): bit is string => Boolean(bit));
  const sessionLabel = whenBits.length > 0 ? whenBits.join(' · ') : 'session';
  return {
    title: `Booked in${waitlistNote}`,
    body: `${input.swimmerName} — ${sessionLabel}`,
  };
}

/** Same amount prefix the treasurer follow-up puts in the push title. */
export function feeFollowUpAmount(amount: number, currency = 'NZD'): string {
  return `${currency} ${amount.toFixed(2)}`;
}

export function feeFollowUpCopy(input: {
  clubName: string;
  overdueCount: number;
  amountLabel: string;
}): { title: string; body: string } {
  const title =
    input.overdueCount === 1
      ? `Overdue invoice — ${input.amountLabel}`
      : `${input.overdueCount} overdue invoices — ${input.amountLabel}`;
  const body = `${input.clubName} has overdue fees on your Xero account. Pay via the invoice Xero emailed you, or check the parent app.`;
  return { title, body };
}

export function programWeekCopy(input: {
  squadName: string;
  weekStarting: string;
  reason?: 'publish' | 'update';
}): { title: string; body: string } {
  const title = input.reason === 'update' ? 'Programs updated' : 'Programs published';
  const squadName = input.squadName.trim() || 'Squad';
  const body = `${squadName} · week of ${input.weekStarting} — open in Programs`;
  return { title, body };
}

export type SwapNoticeEvent = 'requested' | 'accepted' | 'declined' | 'withdrawn' | 'reassigned';
export type SwapNoticeRole = 'original' | 'new' | 'head_coach';

export type SwapSessionPhraseLine = {
  squadName: string;
  sessionDate: string;
  startTime: string;
};

function weekdayLabel(sessionDate: string): string {
  const [year, month, day] = sessionDate.split('-').map(Number);
  if (!year || !month || !day) return sessionDate;
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function sessionListPhrase(sessions: readonly SwapSessionPhraseLine[]): string {
  if (sessions.length === 0) return 'a session';
  if (sessions.length === 1) {
    const session = sessions[0]!;
    return `${session.squadName} ${weekdayLabel(session.sessionDate)} ${formatClock12h(session.startTime)}`;
  }
  const day = weekdayLabel(sessions[0]!.sessionDate);
  const bits = sessions.slice(0, 4).map((session) => `${session.squadName} ${formatClock12h(session.startTime)}`);
  const extra = sessions.length > 4 ? `, and ${sessions.length - 4} more` : '';
  return `${sessions.length} sessions on ${day} (${bits.join(', ')}${extra})`;
}

export function swapNoticeTitle(event: SwapNoticeEvent): string {
  switch (event) {
    case 'requested':
      return 'Swap request';
    case 'accepted':
      return 'Swap accepted';
    case 'declined':
      return 'Swap declined';
    case 'withdrawn':
      return 'Swap withdrawn';
    default:
      return 'Coach swap';
  }
}

export function swapNoticeBody(input: {
  event: SwapNoticeEvent;
  role: SwapNoticeRole;
  actorName: string;
  phrase: string;
  fromName: string;
  toName: string;
}): string {
  const { event, role, actorName, phrase, fromName, toName } = input;
  if (event === 'requested') {
    if (role === 'new') return `${actorName} asked you to take ${phrase}.`;
    return `${actorName} asked ${toName} to take ${phrase}.`;
  }
  if (event === 'accepted') {
    if (role === 'original') return `${actorName} accepted ${phrase}.`;
    if (role === 'new') return `You are covering ${phrase} (swapped from ${fromName}).`;
    return `${actorName} accepted the swap for ${phrase}.`;
  }
  if (event === 'declined') {
    if (role === 'head_coach') return `${actorName} declined the swap for ${phrase}.`;
    return `${actorName} declined ${phrase}.`;
  }
  if (event === 'withdrawn') {
    return `${actorName} withdrew the swap for ${phrase}.`;
  }
  if (role === 'original') return `${toName} is covering ${phrase} (swapped from you).`;
  if (role === 'new') return `You are covering ${phrase} (swapped from ${fromName}).`;
  return `${toName} is covering ${phrase} (swapped from ${fromName}).`;
}
